import React, { useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Button, Table } from "react-bootstrap";
import { createAuthenticatedAxiosInstance, fetchUsers } from "../../api/auth";
import { User, UsersResponse } from "../../interfaces/IUser";
import { useAuthStore } from "../../zustand/loginStore";
import CustomPagination from "../general/CustomPagination";
import { useNavigate } from "react-router-dom";
import "./Users.scss";
import { useUserStore } from "../../zustand/userStore";
import LoadingScreen from "../general/LoadingScreen";
import SearchAndFilterBar from "components/general/SearchAndFilterBar";

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchType, setSearchType] = useState<"text" | "select" | "date">(
    "text"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [filterBy, setFilterBy] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const { token } = useAuthStore();
  const { setUserSelected } = useUserStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);

  /**
   * Asynchronous function to load users from the API
   * It sets up query parameters based on current state and filters
   * Updates the component state with the fetched user data
   */
  const loadUsers = useCallback(async () => {
    try {
      const params: any = {
        page: currentPage,
        per_page: perPage,
      };

      if (searchTerm && filterBy) {
        if (filterBy.value === "role" && selectedRoleId) {
          params.role = selectedRoleId;
        } else if (filterBy.value === "name" || filterBy.value === "email") {
          params[filterBy.value] = searchTerm;
        } else if (filterBy.value === "created_at") {
          params.created_from = searchTerm;
          params.created_to = searchTerm;
        }
      }

      const response: UsersResponse = await fetchUsers(axiosInstance, params);
      setUsers(response.users);
      setCurrentPage(response.current_page);
      setTotalPages(response.total_pages);
      setPerPage(response.per_page);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [currentPage, perPage, filterBy, searchTerm, axiosInstance]);

  /**
   * Effect hook to load users when currentPage, perPage, or filterBy changes
   */
  useEffect(() => {
    loadUsers();
  }, []);

  /**
   * Handler function to change the current page
   * @param page - The new page number
   */
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /**
   * Effect hook to implement debounced search
   * Triggers loadUsers after a 500ms delay when searchTerm changes
   */
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      loadUsers();
    }, 500);

    return () => {
      clearTimeout(delayDebounce);
    };
  }, [searchTerm]);

  /**
   * Handler function for search input changes
   * Updates searchTerm and resets currentPage to 1
   * @param event - The input change event
   */
  const handleSearch = (value: string) => {
    if (value) {
      setSearchTerm(value);
      setSelectedRoleId(value);
    } else {
      setSearchTerm(value);
      setSelectedRoleId(null);
    }
    setCurrentPage(1);
  };
  /**
   * Handler function for filter select changes
   * Updates filterBy and resets currentPage to 1
   * @param event - The select change event
   */
  const handleFilterChange = (
    newValue: { value: string; label: string } | null
  ) => {
    setFilterBy(newValue);
    setCurrentPage(1);

    if (newValue && newValue.value === "role") {
      setSearchType("select");
      setSearchOptions([
        { value: "admin", label: "Admin" },
        { value: "asistente", label: "Asistente" },
        { value: "gerente", label: "Gerente" },
        { value: "cliente", label: "Cliente" },
      ]);
    } else if (newValue && newValue.value === "created_at") {
      setSearchType("date");
    } else {
      setSearchType("text");
      setSearchOptions([]);
    }
  };
  /**
   * Handler function for row clicks
   * Navigates to the user detail page
   * @param userId - The ID of the clicked user
   */
  const handleRowClick = (userId: number) => {
    setUserSelected(userId);
    navigate(`/users/${userId}`);
  };
  const handleAddUser = () => {
    navigate("/users/create");
  };

  const filterOptions = [
    { value: "email", label: "Email" },
    { value: "role", label: "Rol" },
    { value: "created_at", label: "Fecha de creación" },
  ];

  return (
    <Container className="px-4 px-md-5">
      <Row className="align-items-center mb-4 mt-5">
        <Col className="text-center mt-5">
          <h2 className="fw-bold">Usuarios registrados</h2>
        </Col>
      </Row>
      {isLoading ? (
        <LoadingScreen loadingText="Cargando información del usuario" />
      ) : (
        <>
          <SearchAndFilterBar
            searchType={searchType}
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            searchOptions={searchOptions}
            filterBy={filterBy}
            onFilterChange={handleFilterChange}
            filterOptions={filterOptions}
            isSearchDisabled={!filterBy}
          />

          <Table striped bordered hover className="mb-5 border-radius-10">
            <thead className="bg-primary text-white">
              <tr>
                <th className="bg-custom-yellow text-center">ID</th>
                <th className="bg-custom-yellow text-center">Email</th>
                <th className="bg-custom-yellow text-center">Rol</th>
                <th className="bg-custom-yellow text-center">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr
                  key={user.id}
                  className={`custom-row ${index % 2 === 0 ? "odd" : "even"}`}
                  onClick={() => handleRowClick(user.id)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="text-center">{user.id}</td>
                  <td className="text-center">{user.email}</td>
                  <td className="text-center">{user.role}</td>
                  <td className="text-center">
                    {new Date(user.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Row className="justify-content-center mb-3">
            <Col xs="auto">
              <Button
                variant="primary"
                className=" fw-bold"
                onClick={handleAddUser}
              >
                Agregar usuario
              </Button>
            </Col>
          </Row>

          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalResults={users.length}
          />
        </>
      )}
    </Container>
  );
};

export default Users;
